/**
 * Program roll-up — every multi-quote capital program, one row per site, with the
 * pipeline total and a one-click open of both PDFs for that site.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Printer, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const fmt$ = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  sent: "bg-blue-100 text-blue-800 border-blue-300",
  awarded: "bg-emerald-100 text-emerald-800 border-emerald-300",
  declined: "bg-rose-100 text-rose-800 border-rose-300",
};

export const ProgramRollupPanel = ({ token, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/capital-quotes/rollup`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      toast.error("Could not load the program roll-up");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading the roll-up…
      </div>
    );
  }
  if (!data?.programs?.length) return null;

  const openBoth = (program) => {
    for (const q of program.quotes) {
      window.open(`${BACKEND}/api${q.pdf_url}`, "_blank", "noopener");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid="program-rollup-panel">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/70 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700">
            Program roll-up
          </p>
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-4 h-4 text-blue-700" />
            Harris Health water softening
          </h2>
          <p className="text-[12px] text-slate-500">
            {data.site_count} {data.site_count === 1 ? "site" : "sites"} — equipment and
            installation quoted separately, one prime contract per site.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Total pipeline
          </p>
          <p className="text-2xl font-black text-slate-900 tabular-nums"
            data-testid="rollup-pipeline-total">
            {fmt$(data.pipeline_total)}
          </p>
        </div>
      </div>

      <div className="hidden md:grid grid-cols-[1.6fr_1fr_1fr_1fr_auto] gap-3 px-4 py-2
        bg-white border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <span>Site · estimate</span>
        <span className="text-right">Equipment</span>
        <span className="text-right">Installation</span>
        <span className="text-right">Program total</span>
        <span className="text-right pr-1">PDFs</span>
      </div>

      <div className="divide-y divide-slate-100">
        {data.programs.map((p) => {
          const eq = p.quotes.find((q) => q.role === "equipment");
          const inst = p.quotes.find((q) => q.role === "installation");
          return (
            <div key={`${p.program_key}-${p.estimate}`}
              className="grid md:grid-cols-[1.6fr_1fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center
                hover:bg-slate-50/60"
              data-testid={`rollup-row-${p.estimate}`}>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{p.site}</p>
                <p className="text-[11px] text-slate-500">
                  Estimate {p.estimate} · issued {p.issue_date || "—"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS[p.status] || STATUS.draft}`}
                    data-testid={`rollup-status-${p.estimate}`}>
                    {p.status}
                  </span>
                  {!!p.open_questions && (
                    <span className="text-[10px] font-bold text-amber-800"
                      data-testid={`rollup-questions-${p.estimate}`}>
                      {p.open_questions} question{p.open_questions > 1 ? "s" : ""} awaiting review
                    </span>
                  )}
                </div>
              </div>
              <div className="text-left md:text-right">
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 md:hidden">Equipment</p>
                <p className="text-sm font-semibold text-slate-800 tabular-nums">
                  {eq ? fmt$(eq.amount) : "—"}
                </p>
                <p className="font-mono text-[10px] text-slate-400">{eq?.quote_number || ""}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 md:hidden">Installation</p>
                <p className="text-sm font-semibold text-slate-800 tabular-nums">
                  {inst ? fmt$(inst.amount) : "—"}
                </p>
                <p className="font-mono text-[10px] text-slate-400">{inst?.quote_number || ""}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 md:hidden">Program total</p>
                <p className="text-base font-black text-slate-900 tabular-nums"
                  data-testid={`rollup-total-${p.estimate}`}>
                  {fmt$(p.program_total)}
                </p>
              </div>
              <div className="md:text-right">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => openBoth(p)} data-testid={`rollup-open-pdfs-${p.estimate}`}>
                  <Printer className="w-3.5 h-3.5 mr-1" />
                  Open both PDFs
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 bg-[#0b3b52] text-white flex items-center justify-between gap-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-sky-100">
          Harris Health water softening — total pipeline across {data.site_count} sites
        </span>
        <strong className="text-xl font-black tabular-nums">{fmt$(data.pipeline_total)}</strong>
      </div>
    </div>
  );
};

export default ProgramRollupPanel;
